import { fetchPlacePredictionsAsync, GeocoderService, geocodePlaceIdAsync, PlacePredictionLite } from '../../helpers/api/google-api';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { CreateLocationRequest } from '../../types';
import { googleTypesSearchArray } from '../../helpers/constants';
import { ifDefined } from 'lit/directives/if-defined';
import { isDefined } from '../../helpers/utils';
import pointCache from '../../helpers/state/point-cache';

interface GoogleSearchValue {
    name: string;
    value: PlacePredictionLite;
}

const AUTOCOMPLETE_DEBOUNCE_MS = 275;

@customElement('proxi-input-place')
export class ProxiInputPlace extends LitElement {
    amplitude_callsite = 'proxi-input-place';

    @state()
    termToPointName: Map<string, string> = new Map();

    @query('#autocomplete')
    autocompleteInput: HTMLInputElement | undefined;

    @property({ type: Boolean })
    addIconOnGoogleSelect: boolean = true;

    @property({ type: String })
    label?: string;

    @property({ type: String })
    placeholder?: string;

    @property({ type: Boolean })
    mini: boolean = false;

    @state()
    value: CreateLocationRequest | undefined;

    @state()
    selectedPointName: string | undefined;

    @state()
    inputTerm: string = '';

    @state()
    sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

    @state()
    googlePlaces: PlacePredictionLite[] = [];

    @state()
    geocoder: google.maps.Geocoder | undefined;

    private predictionDebounce?: number;

    @state()
    hideDropdown: boolean = true;

    @property({ type: Array })
    searchTypes?: string[];

    @property({ type: Object })
    customMap?: google.maps.Map | undefined;

    protected firstUpdated() {
        this.resetSearch();
    }

    // Listen for changes from parent
    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('value')) {
            if (this.value) {
                if (this.autocompleteInput) {
                    this.autocompleteInput.value = this.value.search ?? '';
                }
            }
        }
    }

    initializeService() {
        if (!this.sessionToken) {
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        }
    }

    // Search Text Changes
    async onInput(event: Event) {
        const input = event.target as HTMLInputElement;
        this.inputTerm = input.value;
        if (this.inputTerm.length > 0) {
            // check if it looks like a latlng
            const latLngMatch = this.inputTerm.match(/(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
            if (latLngMatch) {
                const lat = parseFloat(latLngMatch[1]);
                const lng = parseFloat(latLngMatch[2]);
                this.value = {
                    search: `${lat}, ${lng}`,
                    latLng: { lat, lng },
                    place_id: 'custom',
                };
                this.dispatchEvent(new CustomEvent('value-changed', {
                    detail: {
                        value: this.value,
                    },
                    bubbles: true,
                    composed: true,
                }));
                // hide dropdown
                this.hideDropdown = true;
                if (this.autocompleteInput) {
                    this.autocompleteInput.value = `${lat}, ${lng}`;
                }
                return;
            }
            // if not latlng, do google place search
            this.initializeService();
            if (this.predictionDebounce !== undefined) {
                window.clearTimeout(this.predictionDebounce);
            }
            this.predictionDebounce = window.setTimeout(async () => {
                await this.googlePlaceSearch(this.inputTerm);
                this.hideDropdown = false;
            }, AUTOCOMPLETE_DEBOUNCE_MS);
        } else {
            this.hideDropdown = true;
            this.value = undefined;
        }
    }

    // Call to Do Google Place Search
    async googlePlaceSearch(term: string) {
        this.googlePlaces = await fetchPlacePredictionsAsync({
            input: term,
            sessionToken: this.sessionToken,
            locationBias: pointCache.map?.getBounds() ?? this.customMap?.getBounds(),
            types: this.searchTypes,
        });
        this.requestUpdate();
    }

    private async getPlaceAddress(place: GoogleSearchValue, result?: google.maps.GeocoderResult): Promise<string> {
        const formattedAddress = result?.formatted_address ?? '';
        const placeName = place.value.main_text;
        const isStreetAddress = result?.types.includes('street_address') === true || result?.types.includes('premise') === true || result?.types.includes('subpremise') === true;
        const addressArray = formattedAddress.split(',');
        const address = addressArray[0];
        const isSame = placeName.includes(address);
        const combinedAddress = isStreetAddress || isSame
            ? formattedAddress
            : `${placeName}, ${formattedAddress}`;
        return combinedAddress;
    }

    // Select Google Place from Options
    private async selectGooglePlace(place: GoogleSearchValue) {
        const result = await this.geocodePlace(place.value.place_id);

        // Combine the place name and the formatted address, avoiding duplication
        const combinedAddress = await this.getPlaceAddress(place, result);

        if (isDefined(result)) {

            this.value = {
                search: combinedAddress,
                place_id: place.value.place_id,
                latLng: {
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng(),
                    },
            };
            if (this.autocompleteInput) {
                this.autocompleteInput.value = combinedAddress;
            }
            // dispatch event to parent
            this.dispatchEvent(new CustomEvent('value-changed', {
                detail: {
                    value: this.value,
                    viewport: result?.geometry.viewport,
                },
                bubbles: true,
                composed: true,
            }));

            // hide dropdown
            this.hideDropdown = true;
            this.sessionToken = undefined;
        }
    }

    // Geocode Selected Place
    private async geocodePlace(place_id: string): Promise<google.maps.GeocoderResult | undefined>{
        const result = await geocodePlaceIdAsync(place_id);
        return result[0];
    }

    // Reset Search
    resetSearch() {
        if (isDefined(this.autocompleteInput)) {
            this.autocompleteInput.value = '';
            this.inputTerm = '';
        }
        this.requestUpdate();
    }

    disconnectedCallback() {
        if (this.predictionDebounce !== undefined) {
            window.clearTimeout(this.predictionDebounce);
        }
        super.disconnectedCallback();
    }

    public resetValue() {
        this.value = undefined;
        this.requestUpdate();
    }

    render() {
        const googlePlaceValues: GoogleSearchValue[] = this.googlePlaces.map(place => ({ name: place.description, value: place }));
        const showDropdown = this.inputTerm.length > 0 && !this.hideDropdown;
        return html`
            <proxi-textfield
                outlined
                id="autocomplete"
                @input="${this.onInput}"
                class="${showDropdown ? 'autocomplete-with-content' : ''}"
                style="${this.mini ? 'height:100%;width:100%' : ''}"
                type="search"
                required
                placeholder="${ifDefined(this.placeholder)}"
                value="${this.value?.search ?? ''}"
                label=${ifDefined(this.label)}
            ></proxi-textfield>
            ${showDropdown ? html`
                <div id="dropdown" class="dropdown">
                    ${this.autocompleteInput && this.autocompleteInput?.value.length > 0 ? html`
                        ${googlePlaceValues.map(
                            item =>
                            html`
                                <div
                                    class="dropdown-item"
                                    @click="${async () => await this.selectGooglePlace(item)}"
                                >
                                    <span class="icon"><img .src="${googleTypesSearchArray.find(type => (item.value.types[0] ?? '').includes(type.google_place_id))?.icon ?? 'https://img.icons8.com/stickers/marker.png'}"></span>
                                    <span class="result-text">${item.name}</span>
                                </div>
                            `
                        )}
                    ` : ''}

                </div>
            ` : ''}
        `;
    }

    // Public geocoding function that can be called from parent
    public async geocode(address: string): Promise<google.maps.GeocoderResult[]> {
        if (!isDefined(this.geocoder)) {
			await GeocoderService.loadGeocoder();
            this.geocoder = new google.maps.Geocoder();
        }


        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        return await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.geocoder?.geocode({ address }, (results, status) => {
                if (status === 'OK') {
                    resolve(results as google.maps.GeocoderResult[]);
                } else {
                    reject(new Error(`Geocode was not successful for the following reason: ${status}`));
                }
            });
        });
    }

    static get styles() {
        return css`
            :host {
                width: 100%;
                position: relative;
            }

            input {
                margin: 0;
            }

            #autocomplete:focus, #autocomplete:focus-visible {
                outline: none;
            }

            .autocomplete-with-content {
                border-bottom: none !important;
                border-radius: 4px 4px 0 0 !important;
                margin-bottom: 0 !important;
                background-color: #f1f1f1 !important;
            }

            .dropdown {
                max-height: 200px;
                overflow: auto;
                border: 1px solid #ccc;
                border-radius: 0 0 4px 4px;
                border-top: none;
                background-color: white;
                color: black;
                position: absolute;
                z-index: 1000;
            }

            hr {
                background-color: var(--theme-lighter-text-color);
            }

            #dropdown {
                z-index: 1000;
                width: calc(100% - 2px);
                top: 100%;
                left: 0;
                overflow: visible;
            }

            #autocomplete {
                position: relative;
                padding: 0;
            }

            /* width */
            .dropdown::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .dropdown::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .dropdown::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .dropdown-item {
                padding: 2px 5px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 0.75rem;
            }

            .dropdown-section-header{
                padding: 5px 10px;
                font-weight: bold;
                color: var(--theme-lighter-text-color);
                display: flex;
                justify-content: space-between;
            }

            .hidden {
                display: none;
            }

            .more-button {
                cursor: pointer;
                text-decoration: underline;
            }

            .section-name {
                flex: 1;
                text-align: left;
            }

            .result-text {
                flex: 1;
                text-align: left;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .dropdown-item:hover {
                background-color: #f1f1f1;
                font-size: 0.75rem;
            }

            .icon {
                margin-right: 5px;
            }

            .icon img {
                height: 0.75rem;
                width: 0.75rem;
            }

            .icon-end {
                margin-left: 10px;
            }

            @media (max-width: 600px) {
                .icon img {
                    height: 16px;
                    width: 16px;
                }
        `;
    }
}
